import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...args: ClassValue[]) {
  return twMerge(clsx(args));
}

type Booleanish = boolean | 'true' | 'false';

export const dataAttr = (condition: boolean | undefined) =>
  (condition ? 'true' : undefined) as Booleanish;

/**
 * It stringifies the object which is necessary to ensure that in a logging system
 * we see the object in context in a single log event
 */
export function safeStringify(obj: unknown) {
  try {
    if (obj instanceof Error) {
      // Errors don't serialize well, so we extract what we want
      // We stringify so that we can log the error message and stack trace in a single log event
      return JSON.stringify(obj.stack ?? obj.message);
    }
    // Avoid crashing on circular references
    return JSON.stringify(obj);
  } catch {
    return obj;
  }
}

export function obfuscateEmailAddress(email: string, visibleChars = 1) {
  const [localPart, domain] = email.split('@');
  const obfuscatedLocalPart =
    localPart && localPart.length > visibleChars * 2
      ? localPart.slice(0, visibleChars) +
        '***' +
        localPart.slice(-visibleChars)
      : localPart;

  return `${obfuscatedLocalPart}@${domain}`;
}

export async function downloadFile(url: string, filename: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error('Download failed');

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(blobUrl);
  } catch {
    // Fallback: open in new tab
    window.open(url, '_blank');
  }
}

export const truncateFilename = (filename: string, maxLength = 40): string => {
  if (filename.length <= maxLength) return filename;

  const dotIndex = filename.lastIndexOf('.');
  const ext = dotIndex !== -1 ? filename.slice(dotIndex) : '';
  const name = dotIndex !== -1 ? filename.slice(0, dotIndex) : filename;

  const charsToShow = maxLength - ext.length - 1; // -1 for ellipsis
  const frontChars = Math.ceil(charsToShow / 2);
  const backChars = Math.floor(charsToShow / 2);

  return name.slice(0, frontChars) + '…' + name.slice(-backChars) + ext;
};

export const concatNames = (...names: (string | undefined)[]) => {
  return names.filter(Boolean).join(' ');
};

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export const fieldErrorMessage = (field: {
  state: { meta: { errors: unknown[] } };
}): string | undefined => {
  const err = field.state.meta.errors[0];
  if (!err) return undefined;
  if (typeof err === 'string') return err;
  if (typeof err === 'object' && err !== null && 'message' in err) {
    return String((err as { message: unknown }).message ?? '');
  }
  return undefined;
};
